import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ExpenseContextPanel } from '../components/ExpenseContextPanel';
import {
  ArrowLeft,
  Building2,
  Calendar,
  ShieldAlert,
  FileText,
  MessageSquare,
  History,
  CreditCard,
  Building,
} from 'lucide-react';
import { formatINR } from '@/shared/utils/currency';
import { AccountingSubNav } from '../../accounting/components/AccountingSubNav';
import { api } from '@/shared/api/axios';
import { ROUTES } from '@/routes/paths';

/**
 * Every tab below except Expenses used to render hardcoded sample rows
 * (fake invoice numbers, a fake HDFC/ICICI bank pair, a fake Mumbai
 * address) regardless of which real party you opened — a 2026-08-23 audit
 * finding. Transactions/Addresses/Bank Accounts now come from the real
 * backend response (extended the same day — see api-accounting/parties.js);
 * Documents/Communications have no backend at all yet and say so plainly
 * instead of showing invented rows.
 */
export function PartyDetailWorkspacePage() {
  const { id } = useParams();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [auditEntries, setAuditEntries] = useState<any[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<
    'overview' | 'transactions' | 'subledger' | 'aging' | 'expenses' | 'documents' | 'communications' | 'audit'
  >('overview');

  useEffect(() => {
    if (!id) return;
    api.get(`/api/accounting/parties/${id}`)
      .then((res) => {
        const resData = res.data;
        if (resData.ok || resData.success) {
          setData(resData.data);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [id]);

  // Real Audit Trail Logs (the same feed AuditPage.tsx already shows),
  // filtered to this party — fetched only when the tab is opened.
  useEffect(() => {
    if (activeTab !== 'audit' || !id) return;
    setAuditLoading(true);
    api.get(`/api/accounting/audit-logs?entityType=AccParty&entityId=${encodeURIComponent(id)}&pageSize=20`)
      .then((res) => {
        const resData = res.data;
        setAuditEntries(Array.isArray(resData?.data) ? resData.data : []);
      })
      .catch(() => setAuditEntries([]))
      .finally(() => setAuditLoading(false));
  }, [activeTab, id]);

  if (loading) {
    return (
      <div className="">
        <AccountingSubNav />
        <div className="p-12 text-center text-slate-500">Loading party detail workspace...</div>
      </div>
    );
  }

  if (!data || !data.party) {
    return (
      <div className="">
        <AccountingSubNav />
        <div className="p-12 text-center text-slate-500">Party not found.</div>
      </div>
    );
  }

  const { party, agingAr, creditCheck, addresses = [], bankAccounts = [], transactions = [] } = data;

  // Subledger running balance is derived from the same transaction lines the
  // Transactions tab shows, just re-sorted oldest-first so the balance
  // accumulates in document order instead of the API's newest-first order.
  const subledgerRows = [...transactions]
    .sort((a: any, b: any) => new Date(a.journalDate).getTime() - new Date(b.journalDate).getTime())
    .reduce((acc: any[], line: any) => {
      const prevBalance = acc.length ? acc[acc.length - 1].balance : 0;
      acc.push({ ...line, balance: prevBalance + (line.debit || 0) - (line.credit || 0) });
      return acc;
    }, []);

  return (
    <div className="text-slate-900 dark:text-slate-100">
      <AccountingSubNav />
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-4">
          <div className="flex items-center gap-4">
            <Link
              to={ROUTES.accountingV2Parties}
              className="p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl hover:bg-slate-100"
            >
              <ArrowLeft className="w-4 h-4 text-slate-600 dark:text-slate-400" />
            </Link>
            <div>
              <h1 className="text-xl font-bold tracking-tight flex items-center gap-2">
                <Building2 className="w-5 h-5 text-indigo-500" />
                {party.legalName}
              </h1>
              <p className="text-xs text-slate-500 font-mono">
                Contact ID: <span className="font-bold text-slate-900 dark:text-slate-100">{party.contactId}</span> · GSTIN:{' '}
                {party.taxNumber || 'Unregistered'}
              </p>
            </div>
          </div>

          <div className="flex gap-2">
            {party.isCustomer && <span className="px-3 py-1 bg-emerald-100 text-emerald-800 rounded-full font-bold text-xs">Customer</span>}
            {party.isSupplier && <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full font-bold text-xs">Supplier</span>}
          </div>
        </div>

        {/* 7 Workspace Tabs */}
        <div className="flex gap-2 border-b border-slate-200 dark:border-slate-800 pb-2 overflow-x-auto">
          {[
            { id: 'overview', label: 'Overview' },
            { id: 'transactions', label: 'Transactions' },
            { id: 'subledger', label: 'Subledger' },
            { id: 'aging', label: 'Aging' },
            { id: 'expenses', label: 'Expenses' },
            { id: 'documents', label: 'Documents' },
            { id: 'communications', label: 'Communications' },
            { id: 'audit', label: 'Audit Trail' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-indigo-600 text-white shadow-xs'
                  : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Expenses Tab — reuses the shared panel rather than duplicating a list.
            Scoped by contactId so only this party's expenses are requested. */}
        {activeTab === 'expenses' && (
          <ExpenseContextPanel
            scope={{ kind: 'supplier', id: party.contactId }}
            title={`Expenses for ${party.displayName || party.contactId}`}
            limit={20}
          />
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-3">
                <h3 className="text-sm font-bold border-b pb-2">Company Information</h3>
                <div className="text-xs space-y-2">
                  <div><span className="text-slate-500">Legal Name:</span> {party.legalName}</div>
                  <div><span className="text-slate-500">Display Name:</span> {party.displayName}</div>
                  <div><span className="text-slate-500">Email:</span> {party.email || '—'}</div>
                  <div><span className="text-slate-500">Mobile:</span> {party.mobile || '—'}</div>
                  <div><span className="text-slate-500">GSTIN / VAT:</span> {party.taxNumber || 'Unregistered'}</div>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-3">
                <h3 className="text-sm font-bold border-b pb-2 flex items-center gap-1.5">
                  <ShieldAlert className="w-4 h-4 text-amber-500" /> Credit Control Policy
                </h3>
                <div className="text-xs space-y-2">
                  <div><span className="text-slate-500">Credit Limit:</span> {formatINR((party.creditLimitMinor || 0) / 100)}</div>
                  <div><span className="text-slate-500">Hold Policy:</span> <span className="uppercase font-bold">{party.creditHoldPolicy}</span></div>
                  <div><span className="text-slate-500">Evaluation:</span> <span className="font-bold text-emerald-600">{creditCheck?.status || 'PASS'}</span></div>
                  <div><span className="text-slate-500">Payment Terms:</span> {party.paymentTermsDays} Net Days</div>
                </div>
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-3">
                <h3 className="text-sm font-bold border-b pb-2 flex items-center gap-1.5">
                  <CreditCard className="w-4 h-4 text-indigo-500" /> Financial Summary
                </h3>
                <div className="text-xs space-y-2 font-mono">
                  <div><span className="text-slate-500 font-sans">Outstanding AR Balance:</span> {formatINR((agingAr?.totalMinor || 0) / 100)}</div>
                  <div><span className="text-slate-500 font-sans">Currency:</span> {party.currency}</div>
                </div>
              </div>
            </div>

            {/* Multiple Addresses & Bank Accounts Sections */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-3">
                <h3 className="text-sm font-bold border-b pb-2 flex items-center gap-1.5">
                  <Building className="w-4 h-4 text-slate-500" /> Multiple Addresses
                </h3>
                {addresses.length === 0 ? (
                  <p className="text-xs text-slate-400 py-2">No addresses on file for this party.</p>
                ) : (
                  <div className="space-y-2 text-xs">
                    {addresses.map((addr: any) => (
                      <div key={addr._id} className="p-2.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700">
                        <span className={`font-bold block capitalize ${addr.isDefault ? 'text-indigo-600' : 'text-slate-700 dark:text-slate-300'}`}>
                          {addr.addressType || 'billing'}
                          {addr.isDefault ? ' (Primary)' : ''}
                        </span>
                        <p className="text-slate-600 dark:text-slate-300 mt-0.5">
                          {[addr.street, addr.city, addr.state, addr.postalCode, addr.country].filter(Boolean).join(', ')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-3">
                <h3 className="text-sm font-bold border-b pb-2 flex items-center gap-1.5">
                  <CreditCard className="w-4 h-4 text-slate-500" /> Multiple Bank Accounts (Permission Masked)
                </h3>
                {bankAccounts.length === 0 ? (
                  <p className="text-xs text-slate-400 py-2">No bank accounts on file for this party.</p>
                ) : (
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-50 dark:bg-slate-800 border-b font-bold text-slate-600">
                      <tr>
                        <th className="p-2">Bank</th>
                        <th className="p-2">Account Number</th>
                        <th className="p-2">Primary</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y font-mono">
                      {bankAccounts.map((bank: any) => (
                        <tr key={bank._id}>
                          <td className="p-2 font-sans font-bold">{bank.bankName}</td>
                          <td className="p-2 text-slate-600">{bank.accountNumberMasked}</td>
                          <td className="p-2 font-bold">
                            {bank.isDefault ? <span className="text-emerald-600">✓ Primary</span> : <span className="text-slate-400">—</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="text-sm font-bold">Linked Transactions</h3>
            {transactions.length === 0 ? (
              <p className="text-xs text-slate-400 py-2">No ledger transactions posted against this party yet.</p>
            ) : (
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800 border-b font-bold uppercase tracking-wider">
                  <tr>
                    <th className="p-3">Date</th>
                    <th className="p-3">Journal #</th>
                    <th className="p-3">Account</th>
                    <th className="p-3">Description</th>
                    <th className="p-3 text-right">Debit</th>
                    <th className="p-3 text-right">Credit</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {transactions.map((line: any) => (
                    <tr key={line._id}>
                      <td className="p-3">{new Date(line.journalDate).toLocaleDateString()}</td>
                      <td className="p-3 font-mono font-bold text-indigo-600">{line.journalEntryId?.journalNo || '—'}</td>
                      <td className="p-3 font-mono">{line.accountId ? `${line.accountId.code} · ${line.accountId.name}` : '—'}</td>
                      <td className="p-3 font-semibold">{line.description || line.journalEntryId?.narration || '—'}</td>
                      <td className="p-3 text-right font-mono font-bold">{line.debit ? formatINR(line.debit / 100) : '—'}</td>
                      <td className="p-3 text-right font-mono font-bold">{line.credit ? formatINR(line.credit / 100) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Subledger Tab */}
        {activeTab === 'subledger' && (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="text-sm font-bold">Party Subledger (1100 AR / 2100 AP Control Synchronization)</h3>
            {subledgerRows.length === 0 ? (
              <p className="text-xs text-slate-400 py-2">No subledger activity posted against this party yet.</p>
            ) : (
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-800 border-b font-bold uppercase tracking-wider">
                  <tr>
                    <th className="p-3">Date</th>
                    <th className="p-3">Particulars / Document</th>
                    <th className="p-3 text-right">Debit</th>
                    <th className="p-3 text-right">Credit</th>
                    <th className="p-3 text-right">Running Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y font-mono">
                  {subledgerRows.map((row: any) => (
                    <tr key={row._id}>
                      <td className="p-3 font-sans">{new Date(row.journalDate).toLocaleDateString()}</td>
                      <td className="p-3 font-sans">
                        {row.description || row.journalEntryId?.narration || row.journalEntryId?.journalNo || '—'}
                      </td>
                      <td className="p-3 text-right">{row.debit ? formatINR(row.debit / 100) : '₹0'}</td>
                      <td className="p-3 text-right">{row.credit ? formatINR(row.credit / 100) : '₹0'}</td>
                      <td className="p-3 text-right font-bold text-indigo-600">{formatINR(row.balance / 100)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Aging Tab */}
        {activeTab === 'aging' && (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-indigo-500" /> Subledger Aging Breakdown (Accounts Receivable)
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center font-mono">
              <div className="p-4 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200 dark:border-slate-700">
                <div className="text-xs text-slate-500 font-sans">0–30 Days (Current)</div>
                <div className="text-xl font-bold text-slate-900 dark:text-slate-100">{formatINR((agingAr?.currentMinor || 0) / 100)}</div>
              </div>
              <div className="p-4 bg-amber-50/60 dark:bg-amber-950/40 rounded-xl border border-amber-200 dark:border-amber-800">
                <div className="text-xs text-amber-700 dark:text-amber-300 font-sans">31–60 Days</div>
                <div className="text-xl font-bold text-amber-600">{formatINR((agingAr?.days30Minor || 0) / 100)}</div>
              </div>
              <div className="p-4 bg-orange-50/60 dark:bg-orange-950/40 rounded-xl border border-orange-200 dark:border-orange-800">
                <div className="text-xs text-orange-700 dark:text-orange-300 font-sans">61–90 Days</div>
                <div className="text-xl font-bold text-orange-600">{formatINR((agingAr?.days60Minor || 0) / 100)}</div>
              </div>
              <div className="p-4 bg-rose-50/60 dark:bg-rose-950/40 rounded-xl border border-rose-200 dark:border-rose-800">
                <div className="text-xs text-rose-700 dark:text-rose-300 font-sans">90+ Days Overdue</div>
                <div className="text-xl font-bold text-rose-600">{formatINR((agingAr?.days90PlusMinor || 0) / 100)}</div>
              </div>
            </div>
          </div>
        )}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <FileText className="w-4 h-4 text-indigo-500" /> Attached Documents & Contracts
            </h3>
            <p className="text-xs text-slate-400 py-2">
              Document attachments aren't built yet for parties — there's nowhere to upload or store a file against a
              party record today.
            </p>
          </div>
        )}

        {/* Communications Tab */}
        {activeTab === 'communications' && (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-indigo-500" /> Communication Log & Notes
            </h3>
            <p className="text-xs text-slate-400 py-2">
              Communication logging isn't built yet for parties — there's nowhere to record a call or note against a
              party record today.
            </p>
          </div>
        )}

        {/* Audit Trail Tab */}
        {activeTab === 'audit' && (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 shadow-sm space-y-4">
            <h3 className="text-sm font-bold flex items-center gap-2">
              <History className="w-4 h-4 text-indigo-500" /> Party Record Audit History
            </h3>
            {auditLoading ? (
              <p className="text-xs text-slate-400 py-2">Loading audit history...</p>
            ) : auditEntries.length === 0 ? (
              <p className="text-xs text-slate-400 py-2">
                No audit trail entries recorded for this party yet.
              </p>
            ) : (
              <div className="space-y-2 text-xs font-mono">
                {auditEntries.map((entry: any) => (
                  <div key={entry._id} className="p-2.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border flex justify-between gap-4">
                    <span className="font-sans">
                      <span className="font-bold uppercase">{entry.action}</span>
                      {entry.actorName ? ` by ${entry.actorName}` : ''}
                      {entry.reason ? ` — ${entry.reason}` : ''}
                    </span>
                    <span className="text-slate-400 whitespace-nowrap">{new Date(entry.createdAt).toLocaleString('en-IN')}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default PartyDetailWorkspacePage;
